<template>
  <div class="map-container" ref="mapContainer"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import L from 'leaflet'
import 'leaflet-routing-machine'
import type { Stop } from '@/types'
import { useSettingsStore } from '@/stores/settings'

interface Props {
  stops?: Stop[]
  center?: [number, number]
  zoom?: number
}

const props = withDefaults(defineProps<Props>(), {
  stops: () => [],
  center: () => [47.5, 19.0], // Budapest, Europe center
  zoom: 6
})

const settingsStore = useSettingsStore()
const { mapLanguage, theme } = storeToRefs(settingsStore)

const emit = defineEmits<{
  mapClick: [event: { lat: number; lng: number }]
  stopClick: [stop: Stop]
}>()

const mapContainer = ref<HTMLElement>()
let map: L.Map | null = null
let markers: L.Marker[] = []
let routingControl: any = null
let resizeObserver: ResizeObserver | null = null
let tileLayer: L.TileLayer | null = null
let isUnmounted = false

const stopTypeIcons = {
  city: '🏙️',
  gas_station: '⛽',
  accommodation: '🏨',
  restaurant: '🍽️',
  attraction: '🎯'
}

onMounted(() => {
  console.log('🗺️ MapView onMounted called')
  initializeMap()
  setupResizeObserver()
  console.log('🗺️ MapView initialization complete')
})

onUnmounted(() => {
  console.log('🗺️ MapView onUnmounted - cleaning up...')
  isUnmounted = true
  
  if (resizeObserver && mapContainer.value) {
    resizeObserver.unobserve(mapContainer.value)
    resizeObserver.disconnect()
  }
  
  // Clean up routing control first
  if (routingControl && map) {
    try {
      // Check if the routing control is still attached
      if (map.hasLayer && routingControl._line) {
        map.removeControl(routingControl)
      } else if (routingControl.remove) {
        routingControl.remove()
      }
    } catch (error) {
      console.warn('Error removing routing control on unmount:', error)
    } finally {
      routingControl = null
    }
  }
  
  // Clean up markers
  if (map && markers.length > 0) {
    markers.forEach(marker => {
      try {
        map!.removeLayer(marker)
      } catch (error) {
        console.warn('Error removing marker:', error)
      }
    })
    markers = []
  }
  
  // Clean up map
  if (map) {
    try {
      map.remove()
    } catch (error) {
      console.warn('Error removing map:', error)
    } finally {
      map = null
    }
  }
  
  console.log('🗺️ MapView cleanup complete')
})

watch(() => props.stops, (newStops, oldStops) => {
  if (!map) return // Guard: only update if map exists
  
  console.log('🗺️ Stops changed:', oldStops?.length, '→', newStops?.length)
  
  updateMarkers(newStops)
  updateRoute(newStops)
}, { deep: true, immediate: true })

// Watch for map language changes
watch(mapLanguage, (newLang) => {
  console.log('🗺️ Map language changed to:', newLang)
  updateTileLayer()
})

// Watch for theme changes
watch(theme, (newTheme) => {
  console.log('🗺️ Theme changed to:', newTheme)
  updateTileLayer()
})

function isDarkMode(): boolean {
  const currentTheme = theme.value
  if (currentTheme === 'dark') return true
  if (currentTheme === 'light') return false
  // For 'auto', check system preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function getTileLayerUrl() {
  const lang = mapLanguage.value
  const darkMode = isDarkMode()
  
  // Dark mode tiles
  if (darkMode) {
    // CartoDB Dark Matter - excellent dark mode tiles with clear roads
    return 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
  }
  
  // Light mode tiles with better road and border visibility
  // Using OpenStreetMap France style which has clearer roads and borders
  if (lang === 'local') {
    // Show local language names (native) with clear roads
    return 'https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png'
  } else if (lang === 'en') {
    // English labels with clear roads
    return 'https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png'
  } else {
    // For specific languages, use OSM France style
    // They show clearer borders and highways
    return 'https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png'
  }
}

function updateTileLayer() {
  if (!map) return

  // Remove old tile layer
  if (tileLayer) {
    map.removeLayer(tileLayer)
  }

  // Add new tile layer with appropriate language and theme
  const url = getTileLayerUrl()
  const darkMode = isDarkMode()
  
  tileLayer = L.tileLayer(url, {
    maxZoom: 19,
    attribution: darkMode 
      ? '© OpenStreetMap contributors | © CARTO'
      : '© OpenStreetMap contributors | © OpenStreetMap France'
  }).addTo(map)

  console.log('🗺️ Tile layer updated - Language:', mapLanguage.value, 'Dark mode:', darkMode)
}

function initializeMap() {
  if (!mapContainer.value) return

  console.log('🗺️ Initializing map...')
  console.log('🗺️ Container dimensions:', {
    width: mapContainer.value.offsetWidth,
    height: mapContainer.value.offsetHeight,
    clientWidth: mapContainer.value.clientWidth,
    clientHeight: mapContainer.value.clientHeight
  })

  map = L.map(mapContainer.value, {
    zoomControl: true,
    attributionControl: true,
    keyboard: false, // Disable keyboard navigation to prevent focus issues
    tap: false // Disable tap handler on mobile
  }).setView(props.center, props.zoom)

  console.log('🗺️ Map created')

  // Add tile layer with current language and theme setting
  const url = getTileLayerUrl()
  const darkMode = isDarkMode()
  
  tileLayer = L.tileLayer(url, {
    maxZoom: 19,
    attribution: darkMode 
      ? '© OpenStreetMap contributors | © CARTO'
      : (mapLanguage.value === 'en' 
          ? '© OpenStreetMap contributors'
          : '© OpenStreetMap contributors | Wikimedia maps')
  }).addTo(map)

  console.log('🗺️ Tile layer added - Language:', mapLanguage.value, 'Dark mode:', darkMode)

  // Force map to recalculate size after a short delay
  setTimeout(() => {
    if (map) {
      console.log('🗺️ Invalidating map size...')
      map.invalidateSize()
      console.log('🗺️ Map size invalidated. New size:', map.getSize())
    }
  }, 100)

  // Another check after 500ms
  setTimeout(() => {
    if (map) {
      console.log('🗺️ Second size invalidation...')
      map.invalidateSize()
    }
  }, 500)

  // Handle map clicks
  map.on('click', (e: L.LeafletMouseEvent) => {
    emit('mapClick', {
      lat: e.latlng.lat,
      lng: e.latlng.lng
    })
  })

  // Prevent map from stealing focus
  if (mapContainer.value) {
    mapContainer.value.setAttribute('tabindex', '-1')
  }

  // Initial markers and route
  updateMarkers(props.stops)
  updateRoute(props.stops)
}

function setupResizeObserver() {
  if (!mapContainer.value) return

  console.log('🗺️ Setting up ResizeObserver...')
  
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      console.log('🗺️ Container resized:', {
        width: entry.contentRect.width,
        height: entry.contentRect.height
      })
      
      if (map && entry.contentRect.height > 0 && entry.contentRect.width > 0) {
        // Delay to ensure CSS is applied
        setTimeout(() => {
          if (map) {
            console.log('🗺️ Invalidating size due to resize...')
            map.invalidateSize()
          }
        }, 50)
      }
    }
  })

  resizeObserver.observe(mapContainer.value)
  console.log('🗺️ ResizeObserver setup complete')
}

function updateMarkers(stops: Stop[]) {
  if (!map) return

  // Clear existing markers
  markers.forEach(marker => map!.removeLayer(marker))
  markers = []

  // Add new markers
  stops.forEach((stop, index) => {
    const icon = L.divIcon({
      html: `
        <div class="flex items-center justify-center w-8 h-8 bg-primary-600 text-white rounded-full border-2 border-white shadow-lg">
          <span class="text-xs font-bold">${index + 1}</span>
        </div>
      `,
      className: 'custom-stop-marker',
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    })

    const marker = L.marker([stop.lat, stop.lng], { icon })
      .addTo(map!)
      .bindPopup(`
        <div class="p-2">
          <div class="flex items-center gap-2 mb-1">
            <span class="text-lg">${stopTypeIcons[stop.type]}</span>
            <h3 class="font-semibold">${stop.name}</h3>
          </div>
          <p class="text-sm text-gray-600">${stop.address}</p>
          <p class="text-xs text-gray-500 mt-1">Duration: ${stop.duration} min</p>
        </div>
      `)

    marker.on('click', () => emit('stopClick', stop))
    markers.push(marker)
  })

  // Fit map to markers if we have stops
  if (stops.length > 0) {
    const group = new L.FeatureGroup(markers)
    map.fitBounds(group.getBounds().pad(0.1))
  }
}

function updateRoute(stops: Stop[]) {
  if (!map || isUnmounted) return

  // Remove existing routing control safely
  if (routingControl) {
    try {
      // Check if the routing control is still attached to the map
      if (map.hasLayer && routingControl._line) {
        map.removeControl(routingControl)
      } else if (routingControl.remove) {
        // Alternative method
        routingControl.remove()
      }
    } catch (error) {
      console.warn('Error removing routing control:', error)
    } finally {
      routingControl = null
    }
  }

  // Add route if we have at least 2 stops
  if (stops.length >= 2) {
    const waypoints = stops
      .sort((a, b) => a.order - b.order)
      .map(stop => L.latLng(stop.lat, stop.lng))

    // Create routing control with error handling
    try {
      routingControl = (L as any).Routing.control({
        waypoints,
        routeWhileDragging: false,
        addWaypoints: false,
        createMarker: () => null, // Don't create default markers
        lineOptions: {
          styles: [
            {
              color: '#3b82f6',
              weight: 4,
              opacity: 0.8
            }
          ]
        },
        show: false // Don't show instructions panel
      })

      // Add event listeners to handle errors
      routingControl.on('routingstart', () => {
        if (isUnmounted) {
          console.warn('Component unmounted during routing')
        }
      })

      routingControl.on('routingerror', (e: any) => {
        if (!isUnmounted) {
          console.warn('Routing error:', e)
        }
      })

      // Add to map only if map still exists and not unmounted
      if (map && !isUnmounted) {
        routingControl.addTo(map)

        // Hide the routing instructions panel
        const routingContainer = routingControl.getContainer()
        if (routingContainer) {
          routingContainer.style.display = 'none'
        }

        // Fit map to show all waypoints with padding
        // Wait a bit for the route to be calculated
        setTimeout(() => {
          if (map && !isUnmounted && waypoints.length >= 2) {
            const bounds = L.latLngBounds(waypoints)
            map.fitBounds(bounds, { padding: [50, 50] })
          }
        }, 500)
      }
    } catch (error) {
      if (!isUnmounted) {
        console.warn('Error creating routing control:', error)
      }
      routingControl = null
    }
  }
}

function panTo(lat: number, lng: number, zoom?: number) {
  if (map) {
    map.setView([lat, lng], zoom || map.getZoom())
  }
}

function fitBounds(bounds: [[number, number], [number, number]]) {
  if (map) {
    map.fitBounds(bounds)
  }
}

function getCenter() {
  if (!map) return null
  const center = map.getCenter()
  return { lat: center.lat, lng: center.lng }
}

defineExpose({
  panTo,
  fitBounds,
  getCenter
})
</script>

<style scoped>
/* Ensure the map container takes full height */
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  outline: none; /* Remove focus outline */
}

.map-container:focus {
  outline: none;
}

:deep(.leaflet-container) {
  height: 100%;
  width: 100%;
  outline: none !important;
}

:deep(.leaflet-container:focus) {
  outline: none !important;
}

:deep(.custom-stop-marker) {
  background: transparent !important;
  border: none !important;
}

:deep(.leaflet-popup-content) {
  margin: 0;
  padding: 0;
}

:deep(.leaflet-popup-content-wrapper) {
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
</style>